// Pestaña Cerca: las paradas más próximas a tu ubicación (o al centro del mapa si no hay GPS)
// con los próximos buses de cada una. Se refresca cada 30 s mientras está a la vista.

import type { Dataset } from "../data";
import { haversine, type LatLng } from "../geo";
import { fetchEstimations, type ApiEstimation } from "../live/api";
import { ROUTER_CONFIG } from "../config";
import { badge, escapeHtml, minutesText, titleCase } from "./common";

const SHOW = 8; // paradas en la lista
const LIVE = 5; // de ellas, con tiempo real (cada una es una consulta)
const REFRESH_MS = 30_000;

export interface NearPanelOptions {
  ds: Dataset;
  el: HTMLElement;
  // Posición de referencia y si es la del GPS (true) o el centro del mapa (false).
  where: () => { ll: LatLng; gps: boolean };
  askGps: () => void;
  onOpenStop: (stopIdx: number) => void;
}

export interface NearPanelHandle {
  setActive(active: boolean): void;
  refresh(): void;
}

export function mountNearPanel(o: NearPanelOptions): NearPanelHandle {
  let active = false;
  let timer: number | null = null;
  let seq = 0;

  o.el.addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t.closest("[data-act=gps]")) {
      o.askGps();
      return;
    }
    if (t.closest("[data-act=here]")) {
      refresh();
      return;
    }
    const idx = t.closest<HTMLElement>("[data-idx]")?.dataset["idx"];
    if (idx) o.onOpenStop(Number(idx));
  });

  const refresh = (): void => {
    const run = ++seq;
    const { ll, gps } = o.where();
    const near = o.ds.stops
      .filter((s) => s.lines.length > 0)
      .map((s) => ({ s, m: haversine(ll, s.ll) }))
      .sort((a, b) => a.m - b.m)
      .slice(0, SHOW);
    const walkMin = (m: number): number =>
      Math.max(1, Math.round((m * ROUTER_CONFIG.walkDetour) / ROUTER_CONFIG.walkSpeed / 60));

    o.el.innerHTML = `
      <div class="bar">
        <span class="count">${gps ? "Cerca de ti" : "Cerca del centro del mapa"}</span>
        ${gps ? "" : `<button type="button" class="link" data-act="gps">Usar mi ubicación</button>`}
        <button type="button" class="link" data-act="here">${gps ? "Actualizar" : "Buscar aquí"}</button>
      </div>
      <ul class="near">${near
        .map(
          ({ s, m }) => `<li data-idx="${s.idx}">
            <div class="nhead"><b>${escapeHtml(titleCase(s.name || `Parada ${s.number}`))}</b>
              <small>${walkMin(m)} min a pie · ${s.number}</small></div>
            <div class="nlive" data-stop="${s.id}">${s.lines
              .map((l) => badge(l, o.ds.linesById.get(l)?.color ?? "#555", "s"))
              .join("")}</div>
          </li>`,
        )
        .join("")}</ul>`;

    // Próximos buses de las más cercanas.
    for (const { s } of near.slice(0, LIVE)) {
      fetchEstimations(s.id)
        .then((ests) => {
          if (run !== seq) return;
          const box = o.el.querySelector<HTMLElement>(`.nlive[data-stop="${s.id}"]`);
          if (box && ests.length > 0) box.innerHTML = nextBuses(o.ds, ests);
        })
        .catch(() => {});
    }
  };

  const schedule = (): void => {
    if (timer !== null) window.clearInterval(timer);
    timer = active ? window.setInterval(() => !document.hidden && refresh(), REFRESH_MS) : null;
  };

  return {
    setActive(next) {
      active = next;
      if (active) refresh();
      schedule();
    },
    refresh,
  };
}

// «G 2 min · I 7 min»: el primer bus de cada línea, ordenados por cuál llega antes.
function nextBuses(ds: Dataset, ests: readonly ApiEstimation[]): string {
  const best = new Map<string, ApiEstimation>();
  for (const e of ests) {
    const cur = best.get(e.line);
    if (!cur || e.minutes < cur.minutes) best.set(e.line, e);
  }
  return [...best.values()]
    .sort((a, b) => a.minutes - b.minutes)
    .slice(0, 4)
    .map(
      (e) =>
        `<span class="nb">${badge(e.line, ds.linesById.get(e.line)?.color ?? e.color ?? "#555", "s")}<b${e.minutes <= 1 ? ' class="now"' : ""}>${minutesText(e.minutes)}</b></span>`,
    )
    .join("");
}
